import { useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  List,
  ListOrdered,
} from "lucide-react";
import { cleanPastedHtml } from "@/lib/cleanPastedHtml";

/**
 * Minimal WYSIWYG used by devotional fields of type `editor`.
 *   - Outputs clean HTML on every change (calls onChange with `<p>…</p>` strings)
 *   - Toolbar: Bold, Italic, Underline, Bullet list, Numbered list
 *   - Honors the value prop on mount AND on external resets (e.g. switching
 *     between Create and Edit modes in the same dialog)
 *
 * Props:
 *   value     string — initial/external HTML content
 *   onChange  fn(html) — fires on every keystroke / format change
 *   placeholder optional placeholder shown while empty
 *   testId    optional data-testid prefix for the toolbar buttons
 */
export function RichTextEditor({ value, onChange, placeholder, testId = "rte" }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // We never use these blocks in devotionals — drop their toolbar surface
        heading: false,
        codeBlock: false,
        blockquote: false,
        horizontalRule: false,
      }),
      Underline,
    ],
    content: value || "",
    onUpdate: ({ editor: ed }) => {
      const html = ed.getHTML();
      // Tiptap returns "<p></p>" for an empty editor — normalize to "" so
      // required-field validation on the parent works as expected.
      onChange(html === "<p></p>" ? "" : html);
    },
    editorProps: {
      attributes: {
        class:
          "min-h-[140px] text-sm rounded-md border border-input bg-transparent px-3 py-2 focus:outline-none focus-visible:ring-1 focus-visible:ring-ring",
        "data-testid": `${testId}-editor`,
        ...(placeholder ? { "data-placeholder": placeholder } : {}),
      },
      // Scrub Word/Google Docs/Pages clipboard garbage (inline styles, MS
      // namespaces, font tags, classnames) BEFORE TipTap parses it. Returns
      // only the markup our toolbar can produce: p, strong/b, em/i, u, ul,
      // ol, li, br. Plain-text pastes are untouched.
      transformPastedHTML: (html) => cleanPastedHtml(html),
    },
  });

  // Re-sync editor content when the value prop changes externally (e.g. when
  // Edit-dialog hydrates from /api/devotionals/{id}). Skipped when the value
  // already matches to avoid clobbering the user's caret position mid-typing.
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const incoming = value || "";
    if (current === incoming) return;
    if (current === "<p></p>" && incoming === "") return;
    editor.commands.setContent(incoming, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  if (!editor) return null;

  const Btn = ({ active, onClick, label, icon: Icon, dataTestId }) => (
    <button
      type="button"
      data-testid={dataTestId}
      onClick={onClick}
      aria-label={label}
      aria-pressed={active}
      title={label}
      className={
        "inline-flex size-7 items-center justify-center rounded transition-colors " +
        (active
          ? "bg-foreground text-background"
          : "text-muted-foreground hover:bg-muted hover:text-foreground")
      }
    >
      <Icon className="size-3.5" />
    </button>
  );

  return (
    <div className="space-y-1.5">
      <div className="inline-flex items-center gap-0.5 rounded-md border border-border bg-muted/40 p-1">
        <Btn
          icon={Bold}
          label="Bold"
          active={editor.isActive("bold")}
          onClick={() => editor.chain().focus().toggleBold().run()}
          dataTestId={`${testId}-bold`}
        />
        <Btn
          icon={Italic}
          label="Italic"
          active={editor.isActive("italic")}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          dataTestId={`${testId}-italic`}
        />
        <Btn
          icon={UnderlineIcon}
          label="Underline"
          active={editor.isActive("underline")}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          dataTestId={`${testId}-underline`}
        />
        <span className="mx-0.5 h-4 w-px bg-border" aria-hidden />
        <Btn
          icon={List}
          label="Bullet list"
          active={editor.isActive("bulletList")}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          dataTestId={`${testId}-bullet-list`}
        />
        <Btn
          icon={ListOrdered}
          label="Numbered list"
          active={editor.isActive("orderedList")}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          dataTestId={`${testId}-ordered-list`}
        />
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}
